import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { COMPANY_COLORS, COMPANY_LOGO, DOCUMENT_TYPES } from './brandingUtils';

const addHeader = (doc, title, type = 'document') => {
  try {
    // Add company logo if available
    if (COMPANY_LOGO) {
      doc.addImage(COMPANY_LOGO, 'PNG', 20, 10, 40, 40);
    }
  } catch (error) {
    console.warn('Could not add logo to PDF:', error);
    // Continue without logo if there's an error
  }
  
  // Add decorative header background
  doc.setFillColor(...hexToRGB(COMPANY_COLORS.primary, 0.1));
  doc.rect(0, 0, doc.internal.pageSize.width, 60, 'F');
  
  // Add company name and document type
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRGB(COMPANY_COLORS.primary));
  doc.text('LYNCS', 170, 25, { align: 'right' });
  
  doc.setFontSize(14);
  doc.setTextColor(107, 114, 128); // Gray color
  doc.text(type, 170, 35, { align: 'right' });
  
  // Add document title
  doc.setFontSize(28);
  doc.setTextColor(31, 41, 55); // Dark gray
  doc.text(title, 20, 60);
  
  // Add date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 70);
  
  return 90; // Return next Y position
};

const addSection = (doc, title, yPos) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(31, 41, 55);
  doc.text(title, 20, yPos);
  return yPos + 10;
};

export const exportRecipeToPDF = (recipe) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, recipe.title, 'Recipe Card');

  // Recipe metadata in a styled box
  doc.setFillColor(243, 244, 246); // Light gray background
  doc.rect(20, yPos, 170, 30, 'F');
  doc.setFontSize(12);
  doc.setTextColor(31, 41, 55);
  
  const metadataY = yPos + 10;
  doc.text(`Category: ${recipe.category}`, 30, metadataY);
  doc.text(`Prep Time: ${recipe.prepTime}`, 100, metadataY);
  doc.text(`Yield: ${recipe.yield}`, 30, metadataY + 10);
  doc.text(`Author: ${recipe.author}`, 100, metadataY + 10);

  yPos += 40;

  // Main recipe image
  if (recipe.image) {
    doc.addImage(recipe.image, 'JPEG', 20, yPos, 80, 60);
    // Add description next to image
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(recipe.description || '', 110, yPos, {
      maxWidth: 80,
      align: 'left'
    });
    yPos += 70;
  }

  // Ingredients section with styled table
  yPos = addSection(doc, 'Ingredients', yPos);
  doc.autoTable({
    startY: yPos,
    head: [['Item', 'Quantity']],
    body: recipe.ingredients.map(ing => [ing.item, ing.quantity]),
    styles: {
      fontSize: 12,
      cellPadding: 8,
      lineColor: [59, 130, 246],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontSize: 14,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [243, 244, 246],
    },
    margin: { top: 10 },
  });

  yPos = doc.lastAutoTable.finalY + 20;

  // Steps section with images
  yPos = addSection(doc, 'Instructions', yPos);
  
  recipe.steps.forEach((step, index) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Step number in a circle
    doc.setFillColor(59, 130, 246);
    doc.circle(25, yPos + 5, 8, 'F');
    doc.setTextColor(255);
    doc.setFontSize(12);
    doc.text(step.step.toString(), 25, yPos + 7, { align: 'center' });

    // Step description
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'normal');
    
    // If step has image, create two-column layout
    if (step.image) {
      const stepLines = doc.splitTextToSize(step.description, 100);
      doc.text(stepLines, 40, yPos);
      doc.addImage(step.image, 'JPEG', 140, yPos - 5, 50, 40);
      yPos += Math.max(stepLines.length * 7, 45) + 10;
    } else {
      const stepLines = doc.splitTextToSize(step.description, 150);
      doc.text(stepLines, 40, yPos);
      yPos += stepLines.length * 7 + 10;
    }
  });

  // Notes section if exists
  if (recipe.notes) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos = addSection(doc, 'Notes', yPos + 10);
    doc.setFillColor(254, 243, 199); // Light yellow background
    doc.rect(20, yPos, 170, 30, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(120, 53, 15); // Brown text
    const noteLines = doc.splitTextToSize(recipe.notes, 160);
    doc.text(noteLines, 25, yPos + 10);
  }

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
};

export const exportManualToPDF = (manual) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, manual.title, 'Standard Operating Manual');

  // Manual metadata in a styled box
  doc.setFillColor(243, 244, 246);
  doc.rect(20, yPos, 170, 30, 'F');
  doc.setFontSize(12);
  doc.setTextColor(31, 41, 55);
  
  const metadataY = yPos + 10;
  doc.text(`Version: ${manual.version}`, 30, metadataY);
  doc.text(`Department: ${manual.department}`, 100, metadataY);
  doc.text(`Last Updated: ${manual.lastUpdated}`, 30, metadataY + 10);
  doc.text(`Status: ${manual.status}`, 100, metadataY + 10);

  yPos += 40;

  // Table of Contents
  yPos = addSection(doc, 'Table of Contents', yPos);
  manual.sections.forEach((section, index) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`${index + 1}. ${section.title}`, 30, yPos);
    yPos += 10;
  });

  yPos += 10;

  // Sections
  manual.sections.forEach((section, index) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Section header with background
    doc.setFillColor(59, 130, 246);
    doc.rect(20, yPos, 170, 12, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`${index + 1}. ${section.title}`, 25, yPos + 8);
    yPos += 20;

    // Section content with image if exists
    if (section.image) {
      doc.addImage(section.image, 'JPEG', 20, yPos, 80, 60);
      // Text next to image
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      const contentLines = doc.splitTextToSize(section.content, 80);
      doc.text(contentLines, 110, yPos);
      yPos += Math.max(contentLines.length * 7, 70);
    } else {
      // Full width text if no image
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      const contentLines = doc.splitTextToSize(section.content, 170);
      doc.text(contentLines, 20, yPos);
      yPos += contentLines.length * 7 + 10;
    }

    // Add some spacing between sections
    yPos += 20;
  });

  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
};

export const exportChecklistToPDF = (checklist) => {
  const doc = new jsPDF();
  let yPos = 20;

  const addHeaderToPage = () => {
    // Company header
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('LYNCS', 20, 20);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text('Operational Checklist', 20, 30);
  };

  addHeaderToPage();

  // Checklist Title
  yPos = 50;
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(checklist.title, 20, yPos);

  yPos += 20;

  // Metadata box
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(20, yPos, 170, 45, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, yPos, 170, 45, 3, 3, 'S');

  // Metadata content
  const metadata = [
    { label: 'Category', value: checklist.category },
    { label: 'Frequency', value: checklist.frequency },
    { label: 'Department', value: checklist.department || 'All Departments' },
    { label: 'Date', value: new Date().toLocaleDateString() }
  ];

  metadata.forEach((item, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = col * 85 + 30;
    const y = row * 15 + yPos + 15;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${item.label}:`, x, y);
    doc.setFont('helvetica', 'normal');
    doc.text(item.value, x + 35, y);
  });

  yPos += 55;

  // Instructions section
  if (checklist.instructions) {
    doc.setFillColor(255, 247, 237);
    doc.roundedRect(20, yPos, 170, 35, 3, 3, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(234, 88, 12);
    doc.text('📋 Instructions', 30, yPos + 15);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const instructionLines = doc.splitTextToSize(checklist.instructions, 150);
    doc.text(instructionLines, 30, yPos + 25);
    
    yPos += 45;
  }

  // Checklist items table
  doc.autoTable({
    startY: yPos,
    head: [['Task', 'Notes']],
    body: checklist.items.map(item => [
      item.task,
      item.notes || ''
    ]),
    styles: {
      fontSize: 10,
      cellPadding: 8,
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
      valign: 'middle'
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 90 }
    },
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'left'
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    },
    margin: { left: 20, right: 20 },
    didDrawPage: function(data) {
      // Add header to each new page
      if (data.pageCount > 1) {
        addHeaderToPage();
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(31, 41, 55);
        doc.text(checklist.title, 20, 45);
      }
    }
  });

  // Get the final Y position after the table
  yPos = doc.lastAutoTable.finalY + 20;

  // Check if we need a new page for signatures
  if (yPos > doc.internal.pageSize.height - 80) {
    doc.addPage();
    addHeaderToPage();
    yPos = 50;
  }

  // Signature section
  doc.setDrawColor(226, 232, 240);
  
  // Completed by
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(20, yPos, 80, 50, 3, 3, 'F');
  doc.roundedRect(20, yPos, 80, 50, 3, 3, 'S');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(31, 41, 55);
  doc.text('Completed By:', 30, yPos + 15);
  
  doc.line(30, yPos + 35, 90, yPos + 35);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Signature & Date', 30, yPos + 45);

  // Verified by
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(110, yPos, 80, 50, 3, 3, 'F');
  doc.roundedRect(110, yPos, 80, 50, 3, 3, 'S');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Verified By:', 120, yPos + 15);
  
  doc.line(120, yPos + 35, 180, yPos + 35);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Supervisor Signature & Date', 120, yPos + 45);

  // Add page numbers and footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Generated on ${new Date().toLocaleString()} • Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
};

// New template for Audit Reports
export const exportAuditToPDF = (audit) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, audit.title, DOCUMENT_TYPES.AUDIT);

  // Add audit summary
  addSummaryBox(doc, {
    'Audit Type': audit.type,
    'Location': audit.location,
    'Date': audit.date,
    'Auditor': audit.auditor,
    'Score': `${audit.score}%`
  }, yPos);

  // Add findings table
  yPos += 60;
  doc.autoTable({
    startY: yPos,
    head: [['Category', 'Finding', 'Severity', 'Action Required']],
    body: audit.findings.map(finding => [
      finding.category,
      finding.description,
      finding.severity,
      finding.action
    ]),
    // ... styling
  });

  return doc;
};

// New template for Training Documents
export const exportTrainingToPDF = (training) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, training.title, DOCUMENT_TYPES.TRAINING);

  // Add training metadata
  addSummaryBox(doc, {
    'Department': training.department,
    'Level': training.level,
    'Duration': training.duration,
    'Trainer': training.trainer
  }, yPos);

  // Add modules section
  training.modules.forEach((module, index) => {
    // ... module content
  });

  // Add assessment section
  // ... assessment content

  // Add completion certificate
  addCertificate(doc, training);

  return doc;
};

// New template for Inventory Reports
export const exportInventoryToPDF = (inventory) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, inventory.title, DOCUMENT_TYPES.INVENTORY);

  // Add inventory summary
  addSummaryBox(doc, {
    'Date': inventory.date,
    'Location': inventory.location,
    'Total Items': inventory.totalItems,
    'Total Value': inventory.totalValue
  }, yPos);

  // Add inventory table
  yPos += 60;
  doc.autoTable({
    startY: yPos,
    head: [['Item', 'Quantity', 'Unit', 'Value', 'Status']],
    body: inventory.items.map(item => [
      item.name,
      item.quantity,
      item.unit,
      item.value,
      item.status
    ]),
    // ... styling
  });

  return doc;
};

// Helper functions for interactive elements
const addInteractiveElements = (doc) => {
  // Add clickable table of contents
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 255);
  doc.textWithLink('Table of Contents', 20, 100, { pageNumber: 2 });

  // Add document properties
  doc.setProperties({
    title: 'Document Title',
    subject: 'Document Subject',
    author: 'LYNCS',
    keywords: 'keyword1, keyword2',
    creator: 'LYNCS PDF Generator'
  });

  // Add document security
  // doc.setEncryption('userPassword', 'ownerPassword', permissions);
};

const addSummaryBox = (doc, data, yPos) => {
  // Add styled summary box with key information
  doc.setFillColor(...hexToRGB(COMPANY_COLORS.primary, 0.1));
  doc.roundedRect(20, yPos, 170, Object.keys(data).length * 12 + 10, 3, 3, 'F');
  
  Object.entries(data).forEach(([key, value], index) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${key}:`, 30, yPos + 15 + (index * 12));
    doc.setFont('helvetica', 'normal');
    doc.text(value.toString(), 100, yPos + 15 + (index * 12));
  });
};

const addCertificate = (doc, data) => {
  // Add certificate page with decorative border
  doc.addPage();
  // ... certificate design
};

// Utility functions
const hexToRGB = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

export const exportAnnouncementToPDF = (announcement) => {
  const doc = new jsPDF();
  let yPos = 20;

  // Add company header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('LYNCS', 20, yPos);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text('Company Announcement', 20, yPos + 10);

  yPos += 25;

  // Add title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(announcement.title, 20, yPos);

  yPos += 15;

  // Priority badge - centered and wider
  const priorityColors = {
    high: [239, 68, 68],    // red
    medium: [245, 158, 11], // yellow
    low: [59, 130, 246]     // blue
  };

  const color = priorityColors[announcement.priority.toLowerCase()] || [75, 85, 99];
  
  // Draw priority badge - centered and wider
  doc.setFillColor(...color);
  const badgeWidth = 80;
  const pageWidth = doc.internal.pageSize.width;
  const badgeX = (pageWidth - badgeWidth) / 2;
  doc.roundedRect(badgeX, yPos - 5, badgeWidth, 20, 3, 3, 'F');
  doc.setTextColor(255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `${announcement.priority.toUpperCase()} PRIORITY`,
    pageWidth / 2,
    yPos + 5,
    { align: 'center' }
  );

  yPos += 25;

  // Check if we need a new page
  const checkPageBreak = (currentY, neededSpace) => {
    if (currentY + neededSpace > doc.internal.pageSize.height - 20) {
      doc.addPage();
      return 20; // Return new Y position at top of page
    }
    return currentY;
  };

  // Metadata grid with clean layout
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(20, yPos, 170, 50, 3, 3, 'F');
  
  // Subtle border
  doc.setDrawColor(...color, 0.3);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, yPos, 170, 50, 3, 3, 'S');

  doc.setTextColor(31, 41, 55);
  doc.setFontSize(10);
  
  // Metadata grid layout
  const metadata = [
    { label: 'Department', value: announcement.department },
    { label: 'Published', value: announcement.date },
    { label: 'Author', value: announcement.author },
    { label: 'Status', value: announcement.status.toUpperCase() }
  ];

  metadata.forEach((item, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = col * 85 + 30;
    const y = row * 20 + yPos + 15;

    doc.setFont('helvetica', 'bold');
    doc.text(`${item.label}:`, x, y);
    doc.setFont('helvetica', 'normal');
    doc.text(item.value, x + 35, y);
  });

  yPos += 65;

  // Main content
  yPos = checkPageBreak(yPos, 120);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(20, yPos, 170, 100, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(20, yPos, 170, 100, 3, 3, 'S');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(55, 65, 81);
  const contentLines = doc.splitTextToSize(announcement.content, 150);
  doc.text(contentLines, 30, yPos + 15);

  yPos += 110;

  // Attachments section
  if (announcement.attachments?.length > 0) {
    yPos = checkPageBreak(yPos, 50);
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(20, yPos, 170, 40, 3, 3, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(31, 41, 55);
    doc.text('Attachments', 30, yPos + 15);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    announcement.attachments.forEach((attachment, index) => {
      const attachmentY = yPos + 15 + ((index + 1) * 10);
      if (attachmentY > doc.internal.pageSize.height - 20) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(`• ${attachment.name}`, 35, yPos + 15 + ((index + 1) * 10));
    });

    yPos += 50;
  }

  // Engagement stats in a clean layout
  yPos = checkPageBreak(yPos, 45);
  doc.setFillColor(243, 244, 246);
  doc.roundedRect(20, yPos, 170, 35, 3, 3, 'F');
  
  const stats = [
    { label: 'Views', value: announcement.views },
    { label: 'Acknowledged', value: announcement.acknowledgments },
    { label: 'Comments', value: announcement.comments }
  ];

  // Center the stats better
  const statWidth = 45;
  const totalStatsWidth = statWidth * stats.length;
  const startX = (pageWidth - totalStatsWidth) / 2;

  stats.forEach((stat, index) => {
    const x = startX + (index * statWidth);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(31, 41, 55);
    doc.text(stat.value.toString(), x, yPos + 15, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(stat.label, x, yPos + 25, { align: 'center' });
  });

  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Generated on ${new Date().toLocaleString()} • Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  return doc;
}; 